import { computed } from 'vue'
import useBrowser from '@/hooks/useBrowser'

/**
 * Composable for responsive component sizing based on viewport breakpoints
 * @param {Object} options - Configuration options
 * @param {Object} options.sizeMap - Custom mapping of viewport to size (optional)
 * @returns {Object} Computed responsive size properties
 */
export function useResponsiveSize(options = {}) {
  const { currentViewport } = useBrowser()

  // Default size mapping for viewport breakpoints
  const defaultSizeMap = {
    small: 'sm',
    medium: 'sm', 
    large: 'md',
    wide: 'lg',
    ultra: 'lg',
    max: 'lg'
  }

  const sizeMap = options.sizeMap || defaultSizeMap

  // Computed responsive size based on current viewport
  const responsiveSize = computed(() => {
    return sizeMap[currentViewport.value] || sizeMap.large || 'md'
  })

  // Computed classes object for easy binding
  const responsiveClasses = computed(() => {
    const size = responsiveSize.value
    return {
      [`--${size}`]: true,
    }
  })

  // Helper function to get size-specific classes with custom prefix
  const getSizeClasses = (prefix = 'component') => computed(() => {
    const size = responsiveSize.value
    return {
      [`${prefix}--${size}`]: true,
    }
  })

  return {
    responsiveSize,
    responsiveClasses,
    getSizeClasses,
    currentViewport
  }
}

export default useResponsiveSize
